import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { AuthService } from '@app/modules/components/auth/auth.service';
import { i18n } from '@app/shared/language/i18n';
import { RealtimeService } from '@shared/services/realtime.service';
import { NotificationService } from '@shared/services/notification.service';
import {
  RealtimeServerEvent,
  RealtimeSessionForceLogoutPayload,
  RealtimeSessionRefreshRequiredPayload,
  RealtimeSessionTerminationReason,
} from '@core/services/realtime/realtime-socket.types';
import { TeacherStateService } from '@shared/state/teacher-state.service';

@Injectable({
  providedIn: 'root',
})
export class SessionEventsService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly realtimeService = inject(RealtimeService);
  private readonly notificationService = inject(NotificationService);
  private readonly authService = inject(AuthService);
  private readonly teacherStateService = inject(TeacherStateService);

  private readonly terminationMessages: Record<
    RealtimeSessionTerminationReason,
    string
  > = {
    [RealtimeSessionTerminationReason.USER_BLOCKED]:
      i18n.api_errors.auth.user_deleted,
  };

  public start(): void {
    this.realtimeService
      .on<RealtimeSessionForceLogoutPayload>(
        RealtimeServerEvent.SESSION_FORCE_LOGOUT,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((payload) => this.handleForceLogout(payload));

    this.realtimeService
      .on<RealtimeSessionRefreshRequiredPayload>(
        RealtimeServerEvent.SESSION_REFRESH_REQUIRED,
      )
      .pipe(
        switchMap(() => this.authService.refreshTokens()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.teacherStateService.refresh());
  }

  private handleForceLogout(payload: RealtimeSessionForceLogoutPayload): void {
    const message =
      this.terminationMessages[payload?.reason] ??
      i18n.api_errors.auth.user_deleted;

    this.notificationService.showWarningMessage(message);
    this.authService.performLogout();
  }
}
